import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { fetchSubCategoryById } from "../../../services/subcategories";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import { productChoiceChildren, productTitle, subcategoryTitle } from "../Cms/MenuTree/menuTreeUtils";
import "../../../i18n";
import "./AddCategory.css";

const bannerUrl = (filename) => `${config.UPLOADS_SERVER}/category/${filename}`;

const pickLocalized = (details, lang, field, fallback = "") => {
  const list = Array.isArray(details) ? details : [];
  const match = list.find((d) => d?.language === lang && String(d?.[field] || "").trim());
  if (match?.[field]) return match[field];
  const en = list.find((d) => d?.language === "en" && String(d?.[field] || "").trim());
  if (en?.[field]) return en[field];
  const first = list.find((d) => String(d?.[field] || "").trim());
  return first?.[field] || fallback;
};

const SubcategoryDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [subcategory, setSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLangs, setOpenLangs] = useState({});
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSubCategoryById(id);
        if (cancelled) return;
        setSubcategory(data || {});
        setOpenLangs({});
      } catch (err) {
        console.error("Error fetching subcategory:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const usedLangs = languages.filter((lang) =>
    (subcategory?.subCategoryDetails || []).some(
      (d) => d.language === lang.code && String(d.title || d.description || "").trim()
    )
  );
  const langsToShow = usedLangs.length
    ? usedLangs
    : languages.filter((lang) => lang.code === "en");

  const photoSrc =
    typeof subcategory?.bannerImage === "string" && subcategory.bannerImage.trim()
      ? bannerUrl(subcategory.bannerImage.trim())
      : null;
  const fallbackTitle = subcategoryTitle(subcategory, t("subcategoryDetails"));
  const orientation = subcategory?.displayOrientation === "horizontal" ? "horizontal" : "vertical";

  const products = (subcategory?.productList || [])
    .filter((row) => row?.product != null && row.product.isArchived !== true)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((row) => row.product);

  const renderDescription = (html) => {
    const cleaned = DOMPurify.sanitize(html || "");
    const text = cleaned.replace(/<[^>]+>/g, "").trim();
    if (!text) return null;
    return <div className="catform__prose" dangerouslySetInnerHTML={{ __html: cleaned }} />;
  };

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("subcategoryDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (!subcategory?._id) {
    return (
      <>
        <PageTitle activeMenu={t("subcategoryDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/subcategoriesList")}
            >
              {t("subFormList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("subcategoryDetails")} motherMenu={t("kitchen")} />

      <div className="catform catform--sheet">
        <div className="catform__top catform__identity">
          <button
            type="button"
            className={`catform__cover catform__cover--banner${photoSrc ? "" : " is-empty"}`}
            onClick={() => photoSrc && setLightbox(true)}
            aria-label={photoSrc ? t("subFormBanner") : t("prodSheetNoPhoto")}
          >
            {photoSrc ? <img src={photoSrc} alt="" /> : <i className="fas fa-image" aria-hidden />}
          </button>
          <div className="catform__identity-copy">
            <p className="catform__kicker">{t("subSheetSubtitle")}</p>
            <h1 className="catform__title">{fallbackTitle}</h1>
            <div className="catform__facts">
              <span className="catform__fact">{t(orientation)}</span>
              <span className="catform__fact">
                {products.length} {t("subFormStepProducts").toLowerCase()}
              </span>
            </div>
          </div>
          <div className="catform__top-actions">
            <button
              type="button"
              className="btn catform__link"
              onClick={() => navigate("/subcategoriesList")}
            >
              <i className="fas fa-arrow-left" aria-hidden /> {t("subFormList")}
            </button>
            <Link to={`/edit-subcategory/${id}`} className="btn catform__btn catform__btn--edit">
              <i className="fas fa-pen" aria-hidden />
              {t("edit")}
            </Link>
          </div>
        </div>

        <section className="catform__section is-open">
          <h2 className="catform__heading">{t("catSheetLanguages")}</h2>
          <p className="catform__help">{t("sheetLangHint")}</p>
          <div className="catform__pills">
            {langsToShow.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill is-filled ${isLangOpen(lang.code) ? "is-open" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                <i className="fas fa-check" aria-hidden />
              </button>
            ))}
          </div>
          <div className="catform__lang-fields">
            {langsToShow
              .filter((lang) => isLangOpen(lang.code))
              .map((lang) => {
                const title = pickLocalized(
                  subcategory.subCategoryDetails,
                  lang.code,
                  "title",
                  t("noTitle")
                );
                const description = pickLocalized(
                  subcategory.subCategoryDetails,
                  lang.code,
                  "description"
                );
                const descriptionNode = renderDescription(description);
                return (
                  <div
                    className="catform__lang-panel"
                    key={lang.code}
                    dir={lang.code === "ar" ? "rtl" : undefined}
                  >
                    <div className="catform__field">
                      <span className="catform__label">
                        <img src={lang.flag} alt="" />
                        {lang.label} — {t("title")}
                      </span>
                      <p className="catform__readonly">{title}</p>
                    </div>
                    {descriptionNode && (
                      <div className="catform__field">
                        <span className="catform__label">{t("description")}</span>
                        {descriptionNode}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </section>

        <section className="catform__section is-open">
          <h2 className="catform__heading">
            {t("subFormStepProducts")}
            {products.length > 0 && <span className="catform__fold-count">{products.length}</span>}
          </h2>

          <div className="prod-choices__list catform__sheet-choices">
            {products.length === 0 ? (
              <div className="prod-choices__empty">
                <i className="fas fa-utensils" aria-hidden />
                <p>{t("subSheetNoProducts")}</p>
              </div>
            ) : (
              products.map((product) => {
                const choices = productChoiceChildren(product);
                return (
                  <article
                    key={String(product._id)}
                    className={`prod-choice is-checked${choices.length > 0 ? " is-open" : ""}`}
                  >
                    <div className="prod-choice__head">
                      <span className="prod-choice__name">{productTitle(product)}</span>
                      {choices.length > 0 && (
                        <span className="prod-choice__count">{choices.length}</span>
                      )}
                    </div>
                    {choices.length > 0 && (
                      <div className="prod-choice__body">
                        <div className="catform__size-chips">
                          {choices.map((choice) => (
                            <span key={choice.id} className="prod-choice__chip">
                              {choice.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate("/subcategoriesList")}
          >
            {t("subFormList")}
          </button>
          <Link to={`/edit-subcategory/${id}`} className="btn catform__btn catform__btn--edit">
            <i className="fas fa-pen" aria-hidden />
            {t("edit")}
          </Link>
        </div>
      </div>

      {lightbox && photoSrc && (
        <div className="catform-lightbox" onClick={() => setLightbox(false)} role="dialog" aria-modal="true">
          <button type="button" className="catform-lightbox__close" aria-label={t("cancel")}>
            <i className="fas fa-times" />
          </button>
          <img src={photoSrc} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default SubcategoryDetails;
